import React, { useEffect, useState } from 'react';
import api from '../../config/axios';
import Swal from 'sweetalert2';
import { InputMask } from '@react-input/mask';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { FaFilter, FaUserPlus, FaUsers, FaTicketAlt, FaDollarSign, FaHistory } from 'react-icons/fa';
import Paginacao from '../../components/utils/paginacao';

import MeuVisagismoClientesModal from './modals/Clientes';
import MeuVisagismoHistoricoClienteModal from './modals/HistoricoCreditos';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const MeuVisagismo = () => {
    const [dashboardData, setDashboardData] = useState({
        total_usuarios: 0,
        total_clientes_somados: 0,
        total_creditos_comprados: 0,
        valor_total_vendas: 0,
        vendas_por_estado: [],
        vendas_ultimos_7_dias: []
    });

    const [usuarios, setUsuarios] = useState([]);
    const [currentPageUsuarios, setCurrentPageUsuarios] = useState(1);
    const [totalPagesUsuarios, setTotalPagesUsuarios] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalOpenHistoricoCreditos, setIsModalOpenHistoricoCreditos] = useState(false);
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const limit = 10;

    const [eventosHotmart, setEventosHotmart] = useState([]);
    const [currentPageEventos, setCurrentPageEventos] = useState(1);
    const [totalPagesEventos, setTotalPagesEventos] = useState(1);

    const [filtrosUsuarios, setFiltrosUsuarios] = useState({
        nome: '',
        email: '',
    });

    const [filtrosEventos, setFiltrosEventos] = useState({
        nome: '',
        email: '',
    });

    const estadosMap = {
        'Acre': 'AC',
        'Alagoas': 'AL',
        'Amapá': 'AP',
        'Amazonas': 'AM',
        'Bahia': 'BA',
        'Ceará': 'CE',
        'Distrito Federal': 'DF',
        'Espírito Santo': 'ES',
        'Goiás': 'GO',
        'Maranhão': 'MA',
        'Mato Grosso': 'MT',
        'Mato Grosso do Sul': 'MS',
        'Minas Gerais': 'MG',
        'Pará': 'PA',
        'Paraíba': 'PB',
        'Paraná': 'PR',
        'Pernambuco': 'PE',
        'Piauí': 'PI',
        'Rio de Janeiro': 'RJ',
        'Rio Grande do Norte': 'RN',
        'Rio Grande do Sul': 'RS',
        'Rondônia': 'RO',
        'Roraima': 'RR',
        'Santa Catarina': 'SC',
        'São Paulo': 'SP',
        'Sergipe': 'SE',
        'Tocantins': 'TO',
        'Indefinido': 'INDEF.'
    };

    const formatarMoeda = (valor) => {
        return `R$ ${valor.toFixed(2).replace('.', ',')}`
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
    };

    const lineChartOptions = {
        ...chartOptions,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatarMoeda(value)
                }
            }
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context) => formatarMoeda(context.parsed.y)
                }
            }
        }
    };

    const barChartOptions = {
        ...chartOptions,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: false
            },
            tooltip: {
                callbacks: {
                    title: (context) => {
                        const sigla = context[0].label;
                        const estadoCompleto = Object.keys(estadosMap).find(key => estadosMap[key] === sigla) || sigla;
                        return estadoCompleto;
                    },
                    label: (context) => {
                        return `${context.formattedValue} clientes`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Número de Clientes'
                },
                ticks: {
                    stepSize: 1,
                    precision: 0
                }
            }
        }
    };

    const fetchDashboardData = async () => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await api.get('/meu-visagismo/dashboard');
            setDashboardData(response.data);
            Swal.close();
        } catch (error) {
            Swal.close();
        }
    };

    const fetchUsuarios = async (page) => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const params = {
                offset: page,
                limit: limit,
                direction: 'desc',
                ...filtrosUsuarios
            };
            
            const response = await api.get(`/meu-visagismo/usuarios`, { params });            
            setUsuarios(response.data.usuarios || []);
            setTotalPagesUsuarios((Math.ceil(response.data.total / limit)) || 1);
            Swal.close();
        } catch (error) {
            Swal.close();
        }
    };

    const fetchEventosHotmart = async (page) => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const params = {
                offset: page,
                limit: limit,
                direction: 'desc',
                ...filtrosEventos
            };
            
            const response = await api.get(`/meu-visagismo/eventos-hotmart`, { params });            
            setEventosHotmart(response.data.eventos || []);
            setTotalPagesEventos((Math.ceil(response.data.total / limit)) || 1);
            Swal.close();
        } catch (error) {
            Swal.close();
        }
    };

    const barChartData = {
        labels: dashboardData.vendas_por_estado?.map(item => estadosMap[item.estado] || item.estado) || [],
        datasets: [
            {
                data: dashboardData.vendas_por_estado?.map(item => item.quantidade) || [],
                backgroundColor: 'rgba(54, 162, 235, 0.8)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1
            }
        ]
    };

    const lineChartData = {
        labels: dashboardData.vendas_ultimos_7_dias?.map(item => {
            const data = new Date(item.data);
            return data.toLocaleDateString('pt-BR', { weekday: 'short' });
        }) || [],
        datasets: [
            {
                label: 'Vendas Diárias (R$)',
                data: dashboardData.vendas_ultimos_7_dias?.map(item => item.valor_total) || [],
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1,
            },
        ],
    };

    useEffect(() => {
        fetchDashboardData();
        fetchUsuarios(currentPageUsuarios);
        fetchEventosHotmart(currentPageEventos);
    }, [currentPageUsuarios, currentPageEventos]);

    const handlePageChangeUsuarios = (page) => {
        setCurrentPageUsuarios(page);
    };

    const handleVerClientes = (usuario) => {
        setSelectedUsuario(usuario);
        setIsModalOpen(true);
    };

    const handleVerHistoricoCreditos = (usuario) => {
        setSelectedUsuario(usuario);
        setIsModalOpenHistoricoCreditos(true);
    };

    const handleFiltroUsuariosChange = (e) => {
        const { name, value } = e.target;
        setFiltrosUsuarios(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFiltroEventosChange = (e) => {
        const { name, value } = e.target;
        setFiltrosEventos(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFiltrarUsuarios = () => {
        fetchUsuarios(1); 
    };

    const handleFiltrarEventos = () => {
        fetchEventosHotmart(1); 
    };

    const limparFiltrosUsuarios = () => {
        setFiltrosUsuarios({
            nome: '',
            email: '',
            celular: ''
        });
    };

    const limparFiltrosEventos = () => {
        setFiltrosEventos({
            nome: '',
            email: ''
        });
    };

    return (
        <div className="container-fluid">
            <div className="card">
                <div className="card-body">
                    <div className="row mt-3">
                        <div className="col-lg-12 col-12 mb-4">
                            <h4 className="fw-bold">Meu Visagismo</h4>
                        </div>
                        {/* Filtro de período comentado conforme solicitado
                        <div className="col-lg-6 col-12 d-flex justify-content-end align-items-center mb-4">
                            <div className="btn-group">
                                <button 
                                    className={`btn ${filterPeriod === 'hoje' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleFilterChange('hoje')}
                                >
                                    Hoje
                                </button>
                                <button 
                                    className={`btn ${filterPeriod === '7dias' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleFilterChange('7dias')}
                                >
                                    7 Dias
                                </button>
                                <button 
                                    className={`btn ${filterPeriod === '30dias' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => handleFilterChange('30dias')}
                                >
                                    30 Dias
                                </button>
                            </div>
                        </div>
                        */}

                        <div className="col-md-3 mb-4">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '48px', 
                                                height: '48px', 
                                                backgroundColor: 'rgba(52, 152, 219, 0.1)' 
                                            }}
                                        >
                                            <FaUserPlus size={24} color="#3498db" />
                                        </div>
                                        <div>
                                            <h6 className="card-title text-muted mb-0">Novos Usuários</h6>
                                            <h2 className="mt-2 mb-0">{dashboardData.total_usuarios}</h2>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-4">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '48px', 
                                                height: '48px', 
                                                backgroundColor: 'rgba(46, 204, 113, 0.1)' 
                                            }}
                                        >
                                            <FaUsers size={24} color="#2ecc71" />
                                        </div>
                                        <div>
                                            <h6 className="card-title text-muted mb-0">Novos Clientes</h6>
                                            <h2 className="mt-2 mb-0">{dashboardData.total_clientes_somados}</h2>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-4">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '48px', 
                                                height: '48px', 
                                                backgroundColor: 'rgba(155, 89, 182, 0.1)' 
                                            }}
                                        >
                                            <FaTicketAlt size={24} color="#9b59b6" />
                                        </div>
                                        <div>
                                            <h6 className="card-title text-muted mb-0">Créditos Comprados</h6>
                                            <h2 className="mt-2 mb-0">{dashboardData.total_creditos_comprados}</h2>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-4">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '48px', 
                                                height: '48px', 
                                                backgroundColor: 'rgba(230, 126, 34, 0.1)' 
                                            }}
                                        >
                                            <FaDollarSign size={24} color="#e67e22" />
                                        </div>
                                        <div>
                                            <h6 className="card-title text-muted mb-0">Vendas (R$)</h6>
                                            <h2 className="mt-2 mb-0">R$ {dashboardData.valor_total_vendas.toFixed(2).replace('.', ',')}</h2>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <h5 className="card-title">Distribuição de Usuários por Estado</h5>
                                        <div style={{ height: '400px' }}>
                                            <Bar data={barChartData} options={barChartOptions} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 mb-4">
                                <div className="card">
                                    <div className="card-body">
                                        <h5 className="card-title">Vendas dos Últimos 7 Dias</h5>
                                        <div style={{ height: '300px' }}>
                                            <Line data={lineChartData} options={lineChartOptions} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Gráfico de pizza
                            <div className="col-md-6 mb-4">
                                <div className="card">
                                    <div className="card-body">
                                        <h5 className="card-title">Distribuição de Pacotes Vendidos (Em %)</h5>
                                        <div style={{ height: '300px' }}>
                                            <Pie data={pieChartData} options={pieChartOptions} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            */}
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-12">               
                            <div className="row mb-4 align-items-end">
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label">Nome</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="nome"
                                        value={filtrosUsuarios.nome}
                                        onChange={handleFiltroUsuariosChange}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label">E-mail</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        name="email"
                                        value={filtrosUsuarios.email}
                                        onChange={handleFiltroUsuariosChange}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-xl-6 col-12 mt-lg-0 mt-3 text-end">
                                    <button className="btn btn-outline-secondary me-2" onClick={limparFiltrosUsuarios}>
                                        Limpar
                                    </button>
                                    <button className="btn btn-custom-primary" onClick={handleFiltrarUsuarios}>
                                        <FaFilter /> Filtrar
                                    </button>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead className="table-custom-primary">
                                        <tr>
                                            <th className="text-nowrap">Nome</th>
                                            <th className="text-nowrap">E-mail</th>
                                            <th className="text-nowrap">Data Cadastro</th>
                                            <th className="text-center">Clientes</th>
                                            <th className="text-center">Histórico Créditos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usuarios.map((usuario) => (
                                            <tr key={usuario.usuario_uuid}>
                                                <td className="text-nowrap">{usuario.nome}</td>
                                                <td className="text-nowrap">{usuario.email}</td>
                                                <td className="text-nowrap">
                                                    {usuario.data_criacao ? (() => {
                                                        const data = new Date(usuario.data_criacao);
                                                        return data.toLocaleString('pt-BR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit'
                                                        });
                                                    })() : ''}
                                                </td>
                                                <td className="text-nowrap text-center">
                                                    <button
                                                        className="btn btn-custom-secondary btn-sm"
                                                        onClick={() => handleVerClientes(usuario)}
                                                        title="Ver Clientes"
                                                    >
                                                        <FaUsers />
                                                    </button>
                                                </td>
                                                <td className="text-nowrap text-center">
                                                    <button
                                                        className="btn btn-custom-secondary btn-sm"
                                                        onClick={() => handleVerHistoricoCreditos(usuario)}
                                                        title="Ver Histórico Créditos"
                                                    >
                                                        <FaHistory />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-end mt-4">
                                <Paginacao
                                    currentPage={currentPageUsuarios}
                                    totalPages={totalPagesUsuarios}
                                    onPageChange={handlePageChangeUsuarios}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-12">
                            <h5 className="fw-bold mb-4">Eventos da Hotmart</h5>
                            
                            <div className="row mb-4 align-items-end">
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label">Nome</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="nome"
                                        value={filtrosEventos.nome}
                                        onChange={handleFiltroEventosChange}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label">E-mail</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        name="email"
                                        value={filtrosEventos.email}
                                        onChange={handleFiltroEventosChange}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-xl-6 col-12 mt-lg-0 mt-3 text-end">
                                    <button className="btn btn-outline-secondary me-2" onClick={limparFiltrosEventos}>
                                        Limpar
                                    </button>
                                    <button className="btn btn-custom-primary" onClick={handleFiltrarEventos}>
                                        <FaFilter /> Filtrar
                                    </button>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead className="table-custom-primary">
                                        <tr>
                                        <th className="text-nowrap">Tipo Evento</th>
                                            <th className="text-nowrap">Nome</th>
                                            <th className="text-nowrap">E-mail</th>
                                            <th className="text-nowrap">Data e Hora</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {eventosHotmart.map((evento, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap">{evento.event}</td>
                                                <td className="text-nowrap">{evento.nome}</td>
                                                <td className="text-nowrap">{evento.email}</td>
                                                <td className="text-nowrap">
                                                    {evento.createdat ? (() => {
                                                        const data = new Date(evento.createdat);
                                                        return data.toLocaleString('pt-BR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit'
                                                        }).replace(',', '');
                                                    })() : ''}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-end mt-4">
                                <Paginacao
                                    currentPage={currentPageEventos}
                                    totalPages={totalPagesEventos}
                                    onPageChange={(page) => setCurrentPageEventos(page)}
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div style={{ height: '50px' }}></div>

                    {isModalOpen && (
                        <MeuVisagismoClientesModal
                            usuario={selectedUsuario}
                            onClose={() => setIsModalOpen(false)}
                        />
                    )}

                    {isModalOpenHistoricoCreditos && (
                        <MeuVisagismoHistoricoClienteModal
                            usuario={selectedUsuario}
                            onClose={() => setIsModalOpenHistoricoCreditos(false)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeuVisagismo;
